export const PREEMPTION_WINDOWS = ["24h", "7d", "30d", "90d", "custom"] as const;

export type PreemptionWindow = (typeof PREEMPTION_WINDOWS)[number];
export type PreemptionFacet = "provider" | "gpu_type" | "region" | "fabric";

const WINDOW_HOURS: Record<Exclude<PreemptionWindow, "custom">, number> = {
  "24h": 24,
  "7d": 168,
  "30d": 720,
  "90d": 2160,
};

export function readPreemptionWindow(parameters: URLSearchParams, now: number) {
  const requestedWindow = parameters.get("window");
  const window = PREEMPTION_WINDOWS.find((option) => option === requestedWindow) ?? "7d";
  if (window === "custom") {
    const requestedStart = parameters.get("start") ?? "";
    const requestedEnd = parameters.get("end") ?? "";
    const startTime = Date.parse(requestedStart);
    const endTime = Date.parse(requestedEnd);
    const hasTimezones = [requestedStart, requestedEnd].every((value) =>
      /(?:Z|[+-]\d{2}:\d{2})$/i.test(value),
    );
    const error =
      !hasTimezones || !Number.isFinite(startTime) || !Number.isFinite(endTime)
        ? "Choose both a start and end time in UTC."
        : endTime <= startTime
          ? "The end time must be after the start time."
          : endTime - startTime > 366 * 86_400_000
            ? "Choose a window of 366 days or less."
            : null;
    return {
      window,
      start:
        Number.isFinite(startTime) && hasTimezones
          ? new Date(startTime).toISOString()
          : requestedStart,
      end:
        Number.isFinite(endTime) && hasTimezones ? new Date(endTime).toISOString() : requestedEnd,
      error,
    };
  }
  const endTime = Math.floor(now / 60_000) * 60_000;
  return {
    window,
    start: new Date(endTime - WINDOW_HOURS[window] * 3_600_000).toISOString(),
    end: new Date(endTime).toISOString(),
    error: null,
  };
}

export function formatPreemptionDuration(hours: number | null | undefined): string {
  if (hours == null) return "—";
  if (hours < 1 / 60) return "<1m";
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 24) return `${hours.toFixed(1)}h`;
  return `${Math.floor(hours / 24)}d ${Math.floor(hours % 24)}h`;
}

export function formatPreemptionRate(rate: number | null | undefined): string {
  return rate == null ? "—" : rate.toLocaleString("en-US", { maximumFractionDigits: 1 });
}

export function formatPreemptionTimestamp(timestamp: string): string {
  return new Date(timestamp).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
    hourCycle: "h23",
  });
}

export function preemptionFacetLabel(value: string): string {
  return !value || value === "unknown" ? "Unknown" : value;
}
